import { fullLabel } from "../components/WhereItIs";
import { useState, useEffect } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { prov } from "@/lib/provenance";
import { useSearchParams } from "react-router-dom";
import { ScanField } from "@/components/ScanField";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { scanAccept, scanRefuse, scanWarn } from "@/lib/scanFeedback";
import { SoundToggle } from "@/components/SoundToggle";
import { Boxes, MapPin, ArrowRight, CheckCircle2, XCircle, RotateCcw, Loader2, AlertTriangle } from "lucide-react";

export default function MoveTotePage() {
  const [tote, setTote] = useState("");
  const [toteMeta, setToteMeta] = useState(null);   // camera / gun / typed, recorded on the move
  const [dest, setDest] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [params] = useSearchParams();

  // Pre-fill the container from a scanned-container "Move to a bin/shelf" deep-link.
  useEffect(() => {
    const t = params.get("tote");
    if (t) { setTote(t); scanAccept(`CONTAINER ${t}`); toast.success(`Container set → ${t}`); }
    // eslint-disable-next-line
  }, []);

  const reset = () => { setTote(""); setDest(""); setResult(null); };

  const move = async () => {
    if (!tote || !dest) { toast.error("Scan the container and the destination bin first."); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/containers/move", { container_scan: tote, location_scan: dest,
                                                            provenance: prov("move_tote", toteMeta) });
      if (data.ok) {
        setResult({ ok: true, ...data });
        const line = `Moved ${data.container?.short_code} → ${data.destination?.short_code} · ${data.moved_parts} part${data.moved_parts === 1 ? "" : "s"}`;
        if (data.moved_parts === 0) { scanWarn("MOVED — BUT 0 PARTS CAME WITH IT"); toast.warning(`${line} — empty/wrong tote?`); }
        else { scanAccept(`${data.container?.short_code} → ${data.destination?.short_code}`); toast.success(line); }
      } else {
        scanRefuse(data.message || "THAT CONTAINER DIDN'T MOVE");
        setResult({ ok: false, message: data.message });
      }
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      scanRefuse(msg || "THAT CONTAINER DIDN'T MOVE");
      setResult({ ok: false, message: msg });
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="move-tote-page" className="max-w-xl mx-auto">
      <div className="flex items-start justify-between gap-2">
        <h1 className="font-display text-2xl font-bold tracking-tight mb-1 flex items-center gap-2">
          <Boxes className="w-6 h-6 text-[#B01E28]" />Move Container
        </h1>
        <SoundToggle />
      </div>
      <p className="text-sm text-slate-500 mb-5">Scan a container (tote / bin / pallet), scan a destination bin — the whole container and everything in it moves at once. (Different site? Use Transfers.)</p>

      <div className="border border-slate-200 bg-white rounded-xl p-4 mb-3">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          <Boxes className="w-3.5 h-3.5" />Step 1 — Scan the container {tote && <span className="font-mono text-emerald-600 normal-case">· {tote}</span>}
        </div>
        <ScanField onScan={(v, m) => { setTote(v); setToteMeta(m); scanAccept("Container scanned"); toast.success("Container scanned"); }} testid="tote-scan"
          label={null} placeholder="Scan / type container code…" submitLabel="Set container" autoFocus={true} />
      </div>

      <div className="border border-slate-200 bg-white rounded-xl p-4 mb-4">
        <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-2">
          <MapPin className="w-3.5 h-3.5" />Step 2 — Scan the destination bin {dest && <span className="font-mono text-emerald-600 normal-case">· {dest}</span>}
        </div>
        <ScanField onScan={(v) => { setDest(v); scanAccept("Destination scanned"); toast.success("Destination scanned"); }} testid="dest-scan"
          label={null} placeholder="Scan / type bin code…" submitLabel="Set bin" autoFocus={false} />
      </div>

      <Button data-testid="move-tote-submit" onClick={move} disabled={busy || !tote || !dest}
        className="w-full rounded-lg bg-[#1F1F1F] hover:bg-black text-white h-12 text-base">
        {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <>Move container <ArrowRight className="w-5 h-5 mx-1.5" /> bin</>}
      </Button>

      {result && (
        <div data-testid="move-tote-result"
          className={`mt-4 rounded-xl p-4 border-2 ${!result.ok ? "border-red-500 bg-red-50" : result.moved_parts === 0 ? "border-amber-500 bg-amber-50" : "border-emerald-500 bg-emerald-50"}`}>
          {result.ok ? (
            result.moved_parts === 0 ? (
              <div className="flex items-start gap-2.5 text-amber-800" data-testid="move-tote-empty-warning">
                <AlertTriangle className="w-6 h-6 shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold">Moved <span className="font-mono">{result.container?.short_code}</span> → <span className="font-mono">{result.destination?.short_code}</span>, but <span className="underline">0 parts came with it.</span></div>
                  <div className="text-amber-700 mt-0.5">Empty container or wrong scan? Double-check before you walk away.</div>
                </div>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 text-emerald-800">
                <CheckCircle2 className="w-6 h-6 shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold">Moved <span className="font-mono">{result.container?.short_code}</span> → <span className="font-mono">{result.destination?.short_code}</span> · <span className="font-bold">{result.moved_parts} part{result.moved_parts === 1 ? "" : "s"} came with it.</span></div>
                  <div className="text-emerald-700 mt-0.5">Now at <span className="font-mono">{fullLabel(result.destination) || result.destination?.short_code}</span>.</div>
                </div>
              </div>
            )
          ) : (
            <div className="flex items-center gap-2.5 text-red-700 text-sm"><XCircle className="w-6 h-6 shrink-0" />{result.message}</div>
          )}
          <Button data-testid="move-tote-again" variant="outline" onClick={reset} className="w-full rounded-lg mt-3 h-10">
            <RotateCcw className="w-4 h-4 mr-1.5" />Move another container
          </Button>
        </div>
      )}
    </div>
  );
}
